import { Badge, Button, EmptyState } from '@sitewise/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { getOfflineDb, type OfflineConflictRow } from '../../../offline/db.js';
import { discardConflict, retryConflict } from '../../../offline/queue.js';
import { useOfflineContext } from '../../../offline/use-offline-context.js';
import { useSyncStore } from '../../../stores/sync-store.js';

/**
 * Phase 10 — manual conflict resolution UI. Each row corresponds to a
 * queued mutation the server rejected with a structured 409. The user
 * can either keep the server state (Discard) or re-try (Retry) the
 * queued change. Re-routing to a fresh form (e.g. "open inspection" on
 * stale_inspection) is a deep link rendered alongside the actions.
 */
export function SyncConflictsPage() {
  const { userId } = useOfflineContext();
  const queryClient = useQueryClient();
  const conflictsQuery = useQuery({
    queryKey: ['sync-conflicts', userId],
    queryFn: async () => loadConflicts(userId!),
    enabled: !!userId,
    refetchInterval: 5_000,
  });
  const conflicts = conflictsQuery.data ?? [];
  const setConflictCount = useSyncStore((s) => s.setConflictCount);

  useEffect(() => {
    setConflictCount(conflicts.length);
  }, [conflicts.length, setConflictCount]);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Sync conflicts</h1>
        <p className="text-sm text-fg-muted">
          Changes you made offline could not be applied because the server state has changed.
          Review each item and choose whether to keep the server version or re-apply your change.
        </p>
      </div>

      {conflicts.length === 0 ? (
        <EmptyState
          title="Everything is in sync"
          description="No conflicts to resolve."
          icon={AlertTriangle}
        />
      ) : (
        <div className="space-y-3">
          {conflicts.map((row) => (
            <ConflictCard
              key={row.id}
              row={row}
              onResolved={() => {
                void queryClient.invalidateQueries({ queryKey: ['sync-conflicts'] });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ConflictCard({
  row,
  onResolved,
}: {
  row: OfflineConflictRow;
  onResolved: () => void;
}) {
  const [busy, setBusy] = useState(false);

  async function handleDiscard() {
    setBusy(true);
    try {
      await discardConflict(row.id);
      onResolved();
    } finally {
      setBusy(false);
    }
  }

  async function handleRetry() {
    setBusy(true);
    try {
      await retryConflict(row.id);
      onResolved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-md border border-border bg-surface-elevated p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Badge variant="danger">{prettyKind(row.conflict.kind)}</Badge>
            <span className="text-xs text-fg-muted">{row.method} {row.endpoint}</span>
          </div>
          <p className="text-sm text-fg">{row.conflict.message}</p>
          {row.conflict.currentServerState ? (
            <details className="text-xs text-fg-muted">
              <summary className="cursor-pointer">Server state</summary>
              <pre className="mt-1 overflow-x-auto rounded bg-surface-muted p-2 text-xs">
                {JSON.stringify(row.conflict.currentServerState, null, 2)}
              </pre>
            </details>
          ) : null}
          {row.conflict.yourPendingChange ? (
            <details className="text-xs text-fg-muted">
              <summary className="cursor-pointer">Your queued change</summary>
              <pre className="mt-1 overflow-x-auto rounded bg-surface-muted p-2 text-xs">
                {JSON.stringify(row.conflict.yourPendingChange, null, 2)}
              </pre>
            </details>
          ) : null}
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" size="sm" disabled={busy} onClick={handleRetry}>
            <ArrowRight className="h-4 w-4" />
            Retry
          </Button>
          <Button variant="ghost" size="sm" disabled={busy} onClick={handleDiscard}>
            <Trash2 className="h-4 w-4" />
            Discard
          </Button>
        </div>
      </div>
    </div>
  );
}

function prettyKind(kind: string): string {
  return kind.replaceAll('_', ' ');
}

async function loadConflicts(userId: string): Promise<OfflineConflictRow[]> {
  return getOfflineDb()
    .conflicts.where('userId')
    .equals(userId)
    .reverse()
    .sortBy('createdAt');
}
